import { useEffect, useRef } from 'react';
import { buildSessionWsProtocol, resolveBackendWsBaseUrl } from '../lib/wsAuth';

/**
 * useNotificationsWS — subscribe to `/api/ws/notifications` and fire callbacks
 * when the server pushes real-time events for the signed-in player.
 *
 * Server pushes:
 *   - { type: "ready", user_id, ts }
 *   - { type: "notification", notification: { id, title, message, ... } }
 *   - { type: "circle_update", event: "referral_signup", depth, new_username, ... }
 *   - { type: "pong", ts } (response to our heartbeat)
 *
 * Params:
 *   - enabled     : boolean — set false to close the socket
 *   - token       : session token from localStorage('session_token')
 *   - onNotification(notif) : fired for direct notifications
 *   - onCircleUpdate(payload) : fired for upline/referral circle pings
 *   - reconnectMs : reconnect delay on disconnect (default 3s)
 */
export default function useNotificationsWS({
  token,
  enabled = true,
  onNotification,
  onCircleUpdate,
  reconnectMs = 3000,
} = {}) {
  const wsRef = useRef(null);
  const statusRef = useRef('closed');
  const reconnectTimer = useRef(null);
  const heartbeat = useRef(null);
  const manualCloseRef = useRef(false);

  useEffect(() => {
    if (!enabled || !token) {
      manualCloseRef.current = true;
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* ignore */ } }
      statusRef.current = 'closed';
      return undefined;
    }

    manualCloseRef.current = false;
    const wsUrl = `${resolveBackendWsBaseUrl()}/api/ws/notifications`;
    const protocol = buildSessionWsProtocol(token);

    let openTimer = null;

    const open = () => {
      statusRef.current = 'connecting';
      const ws = protocol ? new WebSocket(wsUrl, [protocol]) : new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        statusRef.current = 'open';
        // Send a ping every 25s to keep ingress proxies from closing idle sockets.
        heartbeat.current = setInterval(() => {
          try { ws.send(JSON.stringify({ type: 'ping' })); } catch { /* ignore */ }
        }, 25000);
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg?.type === 'notification' && msg.notification) {
            onNotification?.(msg.notification);
          } else if (msg?.type === 'circle_update') {
            onCircleUpdate?.(msg);
          }
        } catch {
          /* malformed — ignore */
        }
      };

      ws.onclose = () => {
        statusRef.current = 'closed';
        if (heartbeat.current) { clearInterval(heartbeat.current); heartbeat.current = null; }
        if (!manualCloseRef.current) {
          reconnectTimer.current = setTimeout(open, reconnectMs);
        }
      };

      ws.onerror = () => {
        // Silent — onclose will follow and trigger reconnect logic.
      };
    };

    // Tiny debounce so React StrictMode's double-mount doesn't open then
    // immediately close a half-open socket (which logs a noisy warning).
    openTimer = setTimeout(open, 80);

    return () => {
      manualCloseRef.current = true;
      if (openTimer) clearTimeout(openTimer);
      if (heartbeat.current) { clearInterval(heartbeat.current); heartbeat.current = null; }
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      const ws = wsRef.current;
      if (ws) {
        // Only close if already past the handshake; closing during CONNECTING
        // throws the "closed before established" warning in browsers.
        if (ws.readyState === 1 /* OPEN */) {
          try { ws.close(); } catch { /* ignore */ }
        } else if (ws.readyState === 0 /* CONNECTING */) {
          ws.onopen = () => { try { ws.close(); } catch { /* ignore */ } };
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, enabled]);

  return statusRef;
}
